import { useMemo } from "react";
import { Percent } from "lucide-react";
import { computePartnerOwedForMonth, computePartnerPaidForMonth } from "../../lib/calculations";
import { today } from "../../lib/dateUtils";
import { formatUSD } from "../../lib/formatters";

// One row per partner (team member with at least one partner_rule):
//   YTD cut owed (sum of rule computations across months)
//   YTD cut paid (sum of one_off_payments matched as partner_payout)
//   Outstanding  (owed - paid)
//
// Renders nothing if no partner rules exist (so this only appears after
// the user configures at least one cut).
export default function PartnerOwedCard({ state }) {
  const team = state.team || [];
  const partnerRules = state.partnerRules || [];

  const partnerRows = useMemo(() => {
    const t = today();
    const year = t.getFullYear();
    const currentMonthIdx = t.getMonth();

    const partnerIds = [...new Set(partnerRules.map((r) => r.partnerId))];
    const rows = [];
    for (const pid of partnerIds) {
      const partner = team.find((m) => m.id === pid);
      if (!partner) continue;
      let owed = 0;
      let paid = 0;
      for (let i = 0; i <= currentMonthIdx; i++) {
        const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
        owed += computePartnerOwedForMonth(pid, mKey, state).owed;
        paid += computePartnerPaidForMonth(pid, mKey, state);
      }
      rows.push({
        partner,
        owed,
        paid,
        outstanding: owed - paid,
      });
    }
    rows.sort((a, b) => b.outstanding - a.outstanding);
    return rows;
  }, [team, partnerRules, state]);

  if (partnerRows.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Percent size={14} className="text-purple-600 dark:text-purple-400" />
        <h3 className="font-display font-semibold text-sm text-brand-ink dark:text-[var(--text-primary)]">
          Partner cuts YTD
        </h3>
        <span className="text-[11px] text-brand-slate">
          Owed (computed from rules) vs paid (matched in Expenses).
        </span>
      </div>
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Partner</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Owed YTD</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Paid YTD</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {partnerRows.map(({ partner, owed, paid, outstanding }) => {
              const isOwed = outstanding > 0.5;
              const isOverpaid = outstanding < -0.5;
              return (
                <tr key={partner.id} className="border-t border-gray-100 dark:border-[var(--border-soft)]">
                  <td className="px-4 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)]">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-brand-ink text-white text-[10px] font-display font-semibold flex items-center justify-center shrink-0">
                        {(partner.name || "?").slice(0, 1).toUpperCase()}
                      </span>
                      <span>
                        <span className="block">{partner.name}</span>
                        {partner.role && <span className="block text-[10px] text-brand-slate">{partner.role}</span>}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">{formatUSD(owed)}</td>
                  <td className="px-4 py-2 text-right">{formatUSD(paid)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${
                    isOwed ? "text-amber-700 dark:text-amber-300" :
                    isOverpaid ? "text-red-600 dark:text-red-400" :
                    "text-emerald-700 dark:text-emerald-300"
                  }`}>
                    {isOwed ? formatUSD(outstanding) : isOverpaid ? `−${formatUSD(Math.abs(outstanding))}` : formatUSD(0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
